// src/pages/people/Customers.jsx
import { React, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import { toast } from "react-toastify";

import {
  createCustomer,
  getAllCustomers,
  updateCustomer,
  deleteCustomerApi,
} from "../../api/customerApi";

// ✅ reuse the same uploader you used in Category.jsx
import { uploadCatalogImage } from "../../api/productApi";

const Customers = () => {
  // --- form state: Add ---
  const [firstNameEN, setFirstNameEN] = useState("");
  const [firstNameAR, setFirstNameAR] = useState("");
  const [lastNameEN, setLastNameEN] = useState("");
  const [lastNameAR, setLastNameAR] = useState("");
  const [addressEN, setAddressEN] = useState("");
  const [addressAR, setAddressAR] = useState("");
  const [cityEN, setCityEN] = useState("");
  const [cityAR, setCityAR] = useState("");
  const [stateEN, setStateEN] = useState("");
  const [stateAR, setStateAR] = useState("");
  const [countryEN, setCountryEN] = useState("");
  const [countryAR, setCountryAR] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState(true);

  // ✅ image (Add): preview (URL for <img src>), key-only (saved to DB)
  const [imagePreview, setImagePreview] = useState("");
  const [imageKey, setImageKey] = useState("");

  // --- table/list state ---
  const [customers, setCustomers] = useState([]);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);

  // --- edit modal state ---
  const [editCustomerId, setEditCustomerId] = useState(null);
  const [editFirstNameEN, setEditFirstNameEN] = useState("");
  const [editFirstNameAR, setEditFirstNameAR] = useState("");
  const [editLastNameEN, setEditLastNameEN] = useState("");
  const [editLastNameAR, setEditLastNameAR] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editAddressEN, setEditAddressEN] = useState("");
  const [editAddressAR, setEditAddressAR] = useState("");
  const [editCityEN, setEditCityEN] = useState("");
  const [editCityAR, setEditCityAR] = useState("");
  const [editStateEN, setEditStateEN] = useState("");
  const [editStateAR, setEditStateAR] = useState("");
  const [editCountryEN, setEditCountryEN] = useState("");
  const [editCountryAR, setEditCountryAR] = useState("");
  const [editPostalCode, setEditPostalCode] = useState("");
  const [editStatus, setEditStatus] = useState(true);

  // ✅ image (Edit): preview + key
  const [editImagePreview, setEditImagePreview] = useState("");
  const [editImageKey, setEditImageKey] = useState("");

  // ---------------------------------------------------------------------------
  // Helpers (same semantics as in Category.jsx)
  // ---------------------------------------------------------------------------
  const normalizeKey = (s) => {
    if (!s) return "";
    let v = String(s).trim();
    v = v.replace(/^\/+/, "");
    v = v.replace(/^catelog\//i, "catalog/");
    if (!/^catalog\//i.test(v)) {
      v = `catalog/${v.split("/").pop()}`;
    }
    return v;
  };

  const toPublicUrl = (v) => {
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;

    const key = normalizeKey(v);
    const base = (process.env.REACT_APP_API_IMAGE_BASE_URL ||
      "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com").replace(/\/+$/, "");
    return `${base}/${key}`;
  };

  // Prefer a descriptive deterministic filename when uploading
  const buildCustomerFileName = (side, file, nameEN, emailVal) => {
    const ext =
      (file.type && file.type.split("/")[1]) ||
      file.name.split(".").pop() ||
      "jpg";
    const stamp = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(0, 14);
    const base = (nameEN || emailVal || "customer")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-_]/g, "");
    // e.g., CUST_john-doe_20251112094500.jpg
    return `CUST_${base || "customer"}_${stamp}.${ext}`;
  };

  // ---------------------------------------------------------------------------
  // Image upload (Add)
  // ---------------------------------------------------------------------------
  const handleAddImageChange = async (e) => {
    const t = (s) => (window?.translate ? window.translate(s) : s);
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      // 1) instant local preview (dataURL)
      const dataUrl = reader.result;
      setImagePreview(dataUrl);

      try {
        // 2) upload as dataURL
        const desiredName = buildCustomerFileName("add", file, firstNameEN || lastNameEN, email);
        const { key } = await uploadCatalogImage(dataUrl, desiredName);

        // 3) store canonical key and swap preview to public URL
        const k = normalizeKey(key);
        setImageKey(k);
        setImagePreview(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(t("Image upload failed"));
        setImagePreview("");
        setImageKey("");
      }
    };
    reader.readAsDataURL(file);
  };

  // ---------------------------------------------------------------------------
  // Image upload (Edit)
  // ---------------------------------------------------------------------------
  const handleEditImageChange = async (e) => {
    const t = (s) => (window?.translate ? window.translate(s) : s);
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const dataUrl = reader.result;
      setEditImagePreview(dataUrl); // show instantly

      try {
        const desiredName = buildCustomerFileName(
          "edit",
          file,
          editFirstNameEN || editLastNameEN,
          editEmail
        );
        const { key } = await uploadCatalogImage(dataUrl, desiredName);
        const k = normalizeKey(key);
        setEditImageKey(k);
        setEditImagePreview(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(t("Image upload failed"));
        // keep previous preview/key as-is
      }
    };
    reader.readAsDataURL(file);
  };

  // ---------------------------------------------------------------------------
  // CRUD
  // ---------------------------------------------------------------------------
  const handleCreateCustomer = async () => {
    if (!firstNameEN || !firstNameAR || !lastNameEN || !lastNameAR || !email || !phone) {
      toast.error(`❌ ${window.translate("Please fill in all required fields")}`);
      return;
    }

    // ✅ send the KEY (not dataURL)
    const payload = {
      firstName: { en: firstNameEN, ar: firstNameAR },
      lastName:  { en: lastNameEN,  ar: lastNameAR  },
      email,
      phone,
      address:   { en: addressEN,   ar: addressAR   },
      city:      { en: cityEN,      ar: cityAR      },
      state:     { en: stateEN,     ar: stateAR     },
      country:   { en: countryEN,   ar: countryAR   },
      postalCode,
      image: imageKey || "",                 // <-- S3 key-only
      status: status ? "active" : "inactive"
    };

    try {
      await createCustomer(payload);
      fetchCustomers();
      toast.success(` ${window.translate("Customer created successfully")}`);

      // optional clean-up
      setFirstNameEN(""); setFirstNameAR("");
      setLastNameEN(""); setLastNameAR("");
      setAddressEN(""); setAddressAR("");
      setCityEN(""); setCityAR("");
      setStateEN(""); setStateAR("");
      setCountryEN(""); setCountryAR("");
      setPostalCode(""); setEmail(""); setPhone("");
      setStatus(true);
      setImagePreview(""); setImageKey("");

      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to create customer")}`);
    }
  };

  const fetchCustomers = async () => {
    try {
      const result = await getAllCustomers();
      setCustomers(result || []);
    } catch (error) {
      toast.error(`❌ ${window.translate("Failed to fetch customers")}`);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleUpdateCustomer = async () => {
    if (!editCustomerId) return;

    const payload = {
      firstName: { en: editFirstNameEN, ar: editFirstNameAR },
      lastName:  { en: editLastNameEN,  ar: editLastNameAR  },
      email: editEmail,
      phone: editPhone,
      address: { en: editAddressEN, ar: editAddressAR },
      city:    { en: editCityEN,    ar: editCityAR },
      state:   { en: editStateEN,   ar: editStateAR },
      country: { en: editCountryEN, ar: editCountryAR },
      postalCode: editPostalCode,

      // ✅ if user uploaded a new image in edit, use its S3 key; otherwise keep existing key
      image: editImageKey || normalizeKey(editImagePreview || ""),
      status: editStatus ? "active" : "inactive",
    };

    try {
      await updateCustomer(editCustomerId, payload);
      toast.success(` ${window.translate("Customer updated successfully")}`);
      fetchCustomers();
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to update customer")}`);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deleteCustomerId) {
      toast.error(`❌ ${window.translate("Customer ID not found")}`);
      return;
    }
    try {
      await deleteCustomerApi(deleteCustomerId);
      toast.success(` ${window.translate("Customer deleted successfully")}`);
      fetchCustomers();
      setDeleteCustomerId(null);
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      toast.error(`❌ ${window.translate("Failed to delete customer")}`);
    }
  };

  // ---------------------------------------------------------------------------
  // Table
  // ---------------------------------------------------------------------------
  const columns = [
    {
      title: `${window.translate("Image")}`,
      dataIndex: "Image",
      render: (image) => {
        const src = toPublicUrl(image);
        return src ? (
          <img
            src={src}
            alt="Customer"
            style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          <span>{window.translate("No Image")}</span>
        );
      },
    },
    {
      title: `${window.translate("Customer Name")}`,
      dataIndex: "CustomerName",
      sorter: (a, b) => (a.CustomerName || "").length - (b.CustomerName || "").length,
    },
    {
      title: `${window.translate("Code")}`,
      dataIndex: "Code",
      sorter: (a, b) => (a.Code || "").length - (b.Code || "").length,
    },
    {
      title: `${window.translate("Customer")}`,
      dataIndex: "Customer",
      sorter: (a, b) => (a.Customer || "").length - (b.Customer || "").length,
    },
    {
      title: `${window.translate("Email")}`,
      dataIndex: "Email",
      sorter: (a, b) => (a.Email || "").length - (b.Email || "").length,
    },
    {
      title: window.translate("Phone"),
      dataIndex: "Phone",
      sorter: (a, b) => (a.Phone || "").length - (b.Phone || "").length,
    },
    {
      title: `${window.translate("Country")}`,
      dataIndex: "Country",
      render: (country) => country?.en || "N/A",
      sorter: (a, b) => (a.Country?.en || "").localeCompare(b.Country?.en || ""),
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (status) => (
        <span className={`badge ${status === "active" ? "bg-success" : "bg-danger"}`}>
          {(status || "").charAt(0).toUpperCase() + (status || "").slice(1)}
        </span>
      ),
    },
    {
      title: window.translate("Action"),
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-units"
              onClick={() => {
                // Keep your original mapping, but wire image preview/key properly
                setEditCustomerId(record.id);
                setEditFirstNameEN(record.Customer?.en || "");
                setEditFirstNameAR(record.Customer?.ar || "");
                setEditLastNameEN(record.LastName?.en || "");
                setEditLastNameAR(record.LastName?.ar || "");
                setEditEmail(record.Email || "");
                setEditPhone(record.Phone || "");
                setEditAddressEN(record.Address?.en || "");
                setEditAddressAR(record.Address?.ar || "");
                setEditCityEN(record.City?.en || "");
                setEditCityAR(record.City?.ar || "");
                setEditStateEN(record.State?.en || "");
                setEditStateAR(record.State?.ar || "");
                setEditCountryEN(record.Country?.en || "");
                setEditCountryAR(record.Country?.ar || "");
                setEditPostalCode(record.PostalCode || "");
                setEditStatus(record.status === "active");

                // Image (record.Image is what your Table expects)
                const existingKeyOrUrl = record.Image || record.image || record.photo || "";
                setEditImageKey(existingKeyOrUrl ? normalizeKey(existingKeyOrUrl) : "");
                setEditImagePreview(existingKeyOrUrl ? toPublicUrl(existingKeyOrUrl) : "");
              }}
            >
              <Edit className="feather-edit" />
            </Link>

            <Link
              className="confirm-text p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              onClick={() => setDeleteCustomerId(record.id)}
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
      sorter: (a, b) => (a.createdby || "").length - (b.createdby || "").length,
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Customers")}</h4>
                <h6>{window.translate("Manage your customers")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary text-white"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
              >
                <i className="ti ti-circle-plus me-2 ms-2"></i>
                {window.translate("Add Customer")}
              </Link>
            </div>
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                {customers.length > 0 ? (
                  <Table columns={columns} dataSource={customers} />
                ) : (
                  <p className="text-center py-4">{window.translate("")}.</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      {/* Add Customer */}
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Add Customer")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>

                <div className="modal-body">
                  <form>
                    <div className="new-employee-field">
                      <div className="profile-pic-upload image-field flex gap-2">
                        <div className="profile-pic p-2">
                          {imagePreview && (
                            <div className="mt-2 text-center">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                style={{
                                  width: 100,
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid #ccc",
                                }}
                              />
                              <p className="mt-1 text-muted" style={{ fontSize: 12 }}>
                                {window.translate("Preview")}
                              </p>
                            </div>
                          )}
                          {imagePreview && (
                            <button
                              type="button"
                              className="close rounded-1"
                              onClick={() => {
                                setImagePreview("");
                                setImageKey("");
                              }}
                            >
                              <span aria-hidden="true">×</span>
                            </button>
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="image-upload mb-0">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAddImageChange}
                            />
                            <div className="image-uploads">
                              <h4>{window.translate("uploadImage")}</h4>
                            </div>
                          </div>
                          <p className="mt-2">{window.translate("imageNote")}</p>
                        </div>
                      </div>
                    </div>

                    {/* Names */}
                    <div className="row">
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (EN)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={firstNameEN}
                          onChange={(e) => setFirstNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (AR)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={firstNameAR}
                          onChange={(e) => setFirstNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (EN)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={lastNameEN}
                          onChange={(e) => setLastNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (AR)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={lastNameAR}
                          onChange={(e) => setLastNameAR(e.target.value)}
                        />
                      </div>

                      {/* Email & Phone */}
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("Email")}</label>
                        <input
                          type="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("Phone")}</label>
                        <input
                          type="tel"
                          className="form-control"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>

                      {/* Address */}
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (EN)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={addressEN}
                          onChange={(e) => setAddressEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (AR)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={addressAR}
                          onChange={(e) => setAddressAR(e.target.value)}
                        />
                      </div>

                      {/* City */}
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("City")} (EN)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={cityEN}
                          onChange={(e) => setCityEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("City")} (AR)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={cityAR}
                          onChange={(e) => setCityAR(e.target.value)}
                        />
                      </div>

                      {/* State */}
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("State")} (EN)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={stateEN}
                          onChange={(e) => setStateEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("State")} (AR)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={stateAR}
                          onChange={(e) => setStateAR(e.target.value)}
                        />
                      </div>

                      {/* Country */}
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Country")} (EN)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={countryEN}
                          onChange={(e) => setCountryEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Country")} (AR)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={countryAR}
                          onChange={(e) => setCountryAR(e.target.value)}
                        />
                      </div>

                      {/* Postal & Status */}
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Postal Code")}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3 d-flex align-items-center gap-1">
                        <span className="form-label me-3 mb-0">
                          {window.translate("Status")}
                        </span>
                        <div className="status-toggle modal-status">
                          <input
                            type="checkbox"
                            id="toggleStatus"
                            className="check"
                            checked={status}
                            onChange={(e) => setStatus(e.target.checked)}
                          />
                          <label htmlFor="toggleStatus" className="checktoggle"></label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleCreateCustomer}
                  >
                    {window.translate("Add Customer")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Edit Customer */}
      <div className="modal fade" id="edit-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Edit Customer")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>

                <div className="modal-body">
                  <form>
                    <div className="new-employee-field">
                      <div className="profile-pic-upload image-field flex gap-2">
                        <div className="profile-pic p-2">
                          {editImagePreview && (
                            <div className="mt-2 text-center">
                              <img
                                src={editImagePreview}
                                alt="Preview"
                                style={{
                                  width: 100,
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  border: "1px solid #ccc",
                                }}
                              />
                              <p className="mt-1 text-muted" style={{ fontSize: 12 }}>
                                {window.translate("Preview")}
                              </p>
                            </div>
                          )}
                          {editImagePreview && (
                            <button
                              type="button"
                              className="close rounded-1"
                              onClick={() => {
                                setEditImagePreview("");
                                setEditImageKey("");
                              }}
                            >
                              <span aria-hidden="true">×</span>
                            </button>
                          )}
                        </div>

                        <div className="mb-3">
                          <div className="image-upload mb-0">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleEditImageChange}
                            />
                            <div className="image-uploads">
                              <h4>{window.translate("uploadImage")}</h4>
                            </div>
                          </div>
                          <p className="mt-2">{window.translate("imageNote")}</p>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editFirstNameEN}
                          onChange={(e) => setEditFirstNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("First Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editFirstNameAR}
                          onChange={(e) => setEditFirstNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editLastNameEN}
                          onChange={(e) => setEditLastNameEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Last Name")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editLastNameAR}
                          onChange={(e) => setEditLastNameAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Email")}<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Phone")}<span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editAddressEN}
                          onChange={(e) => setEditAddressEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-12 mb-3">
                        <label className="form-label">
                          {window.translate("Address")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editAddressAR}
                          onChange={(e) => setEditAddressAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("City")} (EN)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCityEN}
                          onChange={(e) => setEditCityEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("City")} (AR)</label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={editCityAR}
                          onChange={(e) => setEditCityAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("State")} (EN)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editStateEN}
                          onChange={(e) => setEditStateEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("State")} (AR)</label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={editStateAR}
                          onChange={(e) => setEditStateAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("Country")} (EN)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCountryEN}
                          onChange={(e) => setEditCountryEN(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-6 mb-3">
                        <label className="form-label">{window.translate("Country")} (AR)</label>
                        <input
                          type="text"
                          className="form-control"
                          dir="rtl"
                          value={editCountryAR}
                          onChange={(e) => setEditCountryAR(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-6 mb-3">
                        <label className="form-label">
                          {window.translate("Postal Code")}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPostalCode}
                          onChange={(e) => setEditPostalCode(e.target.value)}
                        />
                      </div>

                      <div className="col-lg-12">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="user2"
                            className="check"
                            checked={editStatus}
                            onChange={(e) => setEditStatus(e.target.checked)}
                          />
                          <label htmlFor="user2" className="checktoggle"></label>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleUpdateCustomer}
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Save Changes")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content p-5 px-3 text-center">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 fw-bold mb-2 mt-1">
                  {window.translate("Delete")}
                </h4>
                <p className="mb-0 fs-16">
                  {window.translate("Are you sure you want to delete customer")}
                </p>
                <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    data-bs-dismiss="modal"
                    onClick={handleDeleteCustomer}
                  >
                    {window.translate("Yes Delete")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Customers;
